import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LocalizedLinkDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/localized-link-display-model';
import { PortalkonfigurationDisplayModel } from '@/api/models/ace/xFound/Portal/Core/CAQ/Models/portalkonfiguration-display-model';
import { VerifyRegistrationRequest } from '@/api/models/ace/xFound/Portal/Core/CAQ/Authentication/Requests/verify-registration-request';
import { ExtendedAuthenticationService } from '../../services/extended-authentication.service';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { emailValidator } from '@/core/validators/customform.validators';
import { I18nService } from '@/core/services/i18nService';

@Component({
    selector: 'pre-register',
    templateUrl: './pre-register.component.html',
    styleUrls: ['./pre-register.component.scss'],
    standalone: false
})
export class PreRegisterComponent implements OnInit {
  error: string;
  preRegistrationForm: UntypedFormGroup;
  redirect : string;
  preRegistrationSubscription : Subscription;
  portalkonfiguration : PortalkonfigurationDisplayModel;

  public get emailValidated() : boolean {
    return this.preRegistrationForm.controls.email.valid || this.preRegistrationForm.controls.email.untouched;
  }

  public get datenschutzValidated() : boolean {
    return this.preRegistrationForm.controls.datenschutz.valid || this.preRegistrationForm.controls.datenschutz.untouched;
  }
  
  public get benutzernameFormControl(): AbstractControl {
    return this.preRegistrationForm.controls["email"];
  }

  public get datenschutzFormControl(): AbstractControl {
    return this.preRegistrationForm.controls["datenschutz"];
  }

  public get benutzernameErrorMessages(): string[] {
    if(!this.benutzernameFormControl.dirty)
      return [];
    let validationErrors: ValidationErrors | null = this.benutzernameFormControl.errors;

    if(!validationErrors)
      return [];

    return this.toBenutzernameErrorMessage(validationErrors);
  }

  public get datenschutzErrorMessages(): string[] {
    if(!this.datenschutzFormControl.dirty)
      return [];
    let validationErrors: ValidationErrors | null = this.datenschutzFormControl.errors;

    if(!validationErrors)
      return [];

    return this.toDatenschutzErrorMessage(validationErrors);
  }

  public get linkDatenschutzbestimmung(): string{
    let datenschutz: LocalizedLinkDisplayModel | undefined = this.languageservice.getCurrentLocalizedTextDisplayModel(this.portalkonfiguration.datenschutzbestimmungen) ?? this.languageservice.instant('undefinedText');

    return datenschutz?.link ?? "";
  };

  public get isLoading(): boolean {
    return this.preRegistrationSubscription && !this.preRegistrationSubscription.closed;
  }

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected authenticationService: ExtendedAuthenticationService,
    protected loadingAnimationService: LoadingAnimationService,
    protected notificationService: NotificationService,
    protected languageservice: I18nService) {

    this.createForm();
  }

  ngOnInit(): void {
    this.portalkonfiguration = this.route.snapshot.parent?.data.portalkonfiguration;
  }

  preRegister(){
    let request = new VerifyRegistrationRequest();

    request.email = this.preRegistrationForm.controls.email.value;
    request.sprache = this.languageservice.GetCurrLanguageAsEnum();
    request.captchaChallengeResponse = this.preRegistrationForm.controls.captchaChallengeResponse.value;
    
    this.preRegistrationSubscription = this.authenticationService.apiAuthenticationVerifyRegistrationPost(request.AsRequestBody())
    .pipe(finalize(()=>{
      this.preRegistrationForm.markAsPristine();
    }))
    .subscribe(result => {
      let promise = this.router.navigateByUrl('/authentication/mailconfirmation?email=' + request.email + '&from=pre-register');
      this.loadingAnimationService.showSpinnerUsingPromise(promise);
    });

    this.loadingAnimationService.showSpinnerUsingSubscription(this.preRegistrationSubscription);
  }

  protected toBenutzernameErrorMessage(validationErrors: ValidationErrors) : string[] {
    let errorMessages: string[] = [];

    for(let key in validationErrors){
      switch(key){
        case "required":
          errorMessages.push(this.languageservice.instant("register.email_required"));
          break;
        case "email":
          errorMessages.push(this.languageservice.instant("register.email_invalid"));
            break;
        default:
          errorMessages.push(this.languageservice.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toDatenschutzErrorMessage(validationErrors: ValidationErrors) : string[] {
    let errorMessages: string[] = [];

    for(let key in validationErrors){
      switch(key){
        case "required":
          errorMessages.push(this.languageservice.instant("register.privacypolicy_required"));
          break;
        default:
          errorMessages.push(this.languageservice.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected createForm(){
    this.preRegistrationForm = this.formBuilder.group({
      email: ['', [emailValidator, Validators.required]],
      datenschutz: ['', [Validators.requiredTrue]],
      captchaChallengeResponse: ['']
    });
  }
}
